import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  BillingDocumentPrintSheet,
  printBillingDocument,
} from "@/components/billing/BillingDocumentPrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  listCashierQueue,
  recordBillingPayment,
  syncBillingDocumentsFromApi,
} from "@/core/billing/billing-documents/billing-documents-store";
import {
  BILLING_DOC_STATUS_LABELS,
  PAYER_TYPE_LABELS,
  type BillingDocument,
  type BillingPaymentAttachment,
} from "@/core/billing/billing-documents/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { formatMoney } from "@/core/pharmacy/format";
import {
  listAccountsForPaymentMethod,
  listTreasuryAccounts,
  listTreasuryMovements,
} from "@/core/treasury/treasury-store";
import {
  PAYMENT_METHOD_LABELS,
  TREASURY_ACCOUNT_TYPE_LABELS,
  type PaymentMethod,
} from "@/core/treasury/types";
import { routes } from "@/router/all_routes";

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;

async function readAttachment(file: File): Promise<BillingPaymentAttachment | null> {
  if (file.size > MAX_ATTACHMENT_BYTES) return null;
  const allowed =
    file.type.startsWith("image/") ||
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf");
  if (!allowed) return null;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        dataUrl: String(reader.result ?? ""),
      });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export function CashierPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<BillingDocument | null>(null);
  const [method, setMethod] = useState<PaymentMethod>("cash");
  const [accountId, setAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [attachment, setAttachment] = useState<BillingPaymentAttachment | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [queueSearch, setQueueSearch] = useState("");

  useEffect(() => {
    void syncBillingDocumentsFromApi().finally(() => setRefreshKey((k) => k + 1));
  }, []);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:billing-documents", refresh);
    window.addEventListener("hospitol:treasury", refresh);
    return () => {
      window.removeEventListener("hospitol:billing-documents", refresh);
      window.removeEventListener("hospitol:treasury", refresh);
    };
  }, []);

  const queue = useMemo(() => {
    void refreshKey;
    return listCashierQueue();
  }, [refreshKey]);

  const accounts = useMemo(() => {
    void refreshKey;
    return listTreasuryAccounts();
  }, [refreshKey]);

  const methodAccounts = useMemo(() => {
    void refreshKey;
    return listAccountsForPaymentMethod(method);
  }, [refreshKey, method]);

  const recentMovements = useMemo(() => {
    void refreshKey;
    return listTreasuryMovements();
  }, [refreshKey]);

  const filteredQueue = useMemo(() => {
    void refreshKey;
    const query = queueSearch.trim().toLowerCase();
    if (!query) return queue;
    return queue.filter((doc) => {
      const patient = getPatientById(doc.patientId);
      const patientName = patient ? patientFullName(patient) : doc.customer.name;
      const haystack = [
        doc.number,
        patientName,
        doc.customer.name,
        doc.payerName,
        doc.serviceLabel,
        PAYER_TYPE_LABELS[doc.payerType],
        BILLING_DOC_STATUS_LABELS[doc.status],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [queue, queueSearch, refreshKey]);

  const queuePagination = useClientPagination(filteredQueue, [queueSearch], 25);
  const { pagedItems: pagedQueue } = queuePagination;

  const movementsPagination = useClientPagination(recentMovements, [], 10);
  const { pagedItems: pagedMovements } = movementsPagination;

  useEffect(() => {
    if (!accountId && methodAccounts[0]) {
      setAccountId(methodAccounts[0].id);
    } else if (accountId && !methodAccounts.some((a) => a.id === accountId)) {
      setAccountId(methodAccounts[0]?.id ?? "");
    }
  }, [methodAccounts, accountId]);

  const openDoc = (doc: BillingDocument) => {
    setSelected(doc);
    setMethod("cash");
    setReference("");
    setAttachment(null);
    setAttachmentError(null);
    setMsg(null);
    setError(null);
  };

  const handleAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAttachmentError(null);
    const parsed = await readAttachment(file);
    if (!parsed) {
      setAttachment(null);
      setAttachmentError("Fichier refusé — PDF ou image, max 2 Mo.");
      return;
    }
    setAttachment(parsed);
  };

  const handlePayment = (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    if (!accountId) {
      setError("Sélectionnez la caisse ou le compte à créditer.");
      return;
    }

    setBusy(true);
    setError(null);
    const updated = recordBillingPayment(selected.id, {
      method,
      treasuryAccountId: accountId,
      amount: selected.subtotal,
      reference,
      attachment,
    });
    setBusy(false);

    if (!updated) {
      setError("Impossible d'enregistrer le paiement.");
      return;
    }

    const account = accounts.find((a) => a.id === accountId);
    setMsg(
      `Paiement enregistré — ${formatMoney(updated.subtotal)} crédité sur ${account?.name ?? "compte"}.`,
    );
    setSelected(null);
    setRefreshKey((k) => k + 1);
  };

  return (
    <div>
      <PageHeader
        title="Caisse — encaissements"
        description="Marquez les factures payées, joignez une preuve de paiement et créditez la caisse ou le compte correspondant."
        actions={
          <Link to={routes.billingAccounting} className="btn btn-outline-secondary btn-sm">
            Comptabilité
          </Link>
        }
      />

      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="row g-3 mb-4">
        {accounts.map((account) => (
          <div key={account.id} className="col-md-3 col-6">
            <div className="ph-invoice-sheet text-center py-3">
              <div className="small text-muted">
                {TREASURY_ACCOUNT_TYPE_LABELS[account.type]}
              </div>
              <div className="fw-semibold">{account.name}</div>
              <div className="h5 fw-bold mb-0 text-success">{formatMoney(account.balance)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet">
            <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between mb-3 ph-cashier-queue-head">
              <h3 className="h6 fw-semibold mb-0">
                File d&apos;attente ({filteredQueue.length}
                {queueSearch.trim() ? ` / ${queue.length}` : ""})
              </h3>
              {queue.length > 0 && (
                <div className="ph-cashier-queue-search">
                  <label className="form-label small fw-semibold mb-1" htmlFor="cashier-queue-search">
                    Rechercher (priorité)
                  </label>
                  <input
                    id="cashier-queue-search"
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="N° document, patient, payeur…"
                    value={queueSearch}
                    onChange={(e) => setQueueSearch(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              )}
            </div>
            {queue.length === 0 ? (
              <p className="text-muted mb-0">
                Aucun document en attente de paiement. Les proformas accueil et factures
                patient apparaissent ici.
              </p>
            ) : filteredQueue.length === 0 ? (
              <p className="text-muted mb-0">
                Aucun document ne correspond à « {queueSearch.trim()} ».
              </p>
            ) : (
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm ph-dash-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Patient</th>
                      <th>Payeur</th>
                      <th>Statut</th>
                      <th className="text-end">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedQueue.map((doc) => {
                      const patient = getPatientById(doc.patientId);
                      return (
                        <tr
                          key={doc.id}
                          className={`ph-diag-row${selected?.id === doc.id ? " table-active" : ""}`}
                        >
                          <td data-label="N°" className="ph-inv-badge">
                            {doc.number}
                          </td>
                          <td data-label="Patient" className="small">
                            {patient ? patientFullName(patient) : doc.customer.name}
                            {doc.source === "consultation" && doc.serviceLabel && (
                              <span className="d-block text-muted">
                                {doc.serviceLabel}
                              </span>
                            )}
                          </td>
                          <td data-label="Payeur" className="small">
                            {PAYER_TYPE_LABELS[doc.payerType]}
                            {doc.payerType !== "patient" && (
                              <span className="d-block">{doc.payerName}</span>
                            )}
                          </td>
                          <td data-label="Statut">
                            <span className="badge bg-secondary">
                              {BILLING_DOC_STATUS_LABELS[doc.status]}
                            </span>
                          </td>
                          <td data-label="Montant" className="text-end fw-medium">
                            {formatMoney(doc.subtotal)}
                          </td>
                          <td data-label="Action" className="text-end">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openDoc(doc)}
                            >
                              Encaisser
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePagination
                  page={queuePagination.page}
                  pageSize={queuePagination.pageSize}
                  totalItems={queuePagination.totalItems}
                  onPageChange={queuePagination.setPage}
                  onPageSizeChange={queuePagination.setPageSize}
                />
              </div>
            )}
          </div>

          {recentMovements.length > 0 && (
            <div className="ph-invoice-sheet mt-3">
              <h3 className="h6 fw-semibold mb-3">Derniers mouvements</h3>
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Libellé</th>
                      <th>Mode</th>
                      <th className="text-end">Montant</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedMovements.map((m) => (
                      <tr key={m.id} className="ph-diag-row">
                        <td data-label="Libellé" className="small">
                          {m.label}
                        </td>
                        <td data-label="Mode" className="small">
                          {m.paymentMethod
                            ? PAYMENT_METHOD_LABELS[m.paymentMethod]
                            : "—"}
                        </td>
                        <td data-label="Montant" className="text-end text-success">
                          +{formatMoney(m.amount)}
                        </td>
                        <td data-label="Date" className="small text-muted">
                          {new Date(m.createdAt).toLocaleString("fr-FR")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination
                  page={movementsPagination.page}
                  pageSize={movementsPagination.pageSize}
                  totalItems={movementsPagination.totalItems}
                  pageSizeOptions={[10, 25, 50]}
                  onPageChange={movementsPagination.setPage}
                  onPageSizeChange={movementsPagination.setPageSize}
                />
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-5">
          {selected ? (
            <div className="ph-invoice-sheet">
              <h3 className="h6 fw-semibold mb-3">Encaissement — {selected.number}</h3>

              <div className="border rounded p-2 bg-light mb-3">
                <BillingDocumentPrintSheet document={selected} />
              </div>

              <form onSubmit={handlePayment}>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Montant à encaisser</label>
                  <div className="form-control form-control-sm bg-light fw-bold">
                    {formatMoney(selected.subtotal)}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold" htmlFor="pay-method">
                    Mode de paiement
                  </label>
                  <select
                    id="pay-method"
                    className="form-select form-select-sm"
                    value={method}
                    onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                  >
                    {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((key) => (
                      <option key={key} value={key}>
                        {PAYMENT_METHOD_LABELS[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold" htmlFor="pay-account">
                    Caisse / compte crédité
                  </label>
                  <select
                    id="pay-account"
                    className="form-select form-select-sm"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    required
                  >
                    {methodAccounts.length === 0 ? (
                      <option value="">Aucun compte pour ce mode</option>
                    ) : (
                      methodAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name} — solde {formatMoney(account.balance)}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="form-text">
                    Le solde du compte sélectionné augmentera de {formatMoney(selected.subtotal)}.
                  </div>
                </div>

                {method !== "cash" && (
                  <div className="mb-3">
                    <label className="form-label small fw-semibold" htmlFor="pay-ref">
                      Référence transaction
                    </label>
                    <input
                      id="pay-ref"
                      className="form-control form-control-sm"
                      placeholder="N° virement, ID Mobile Money…"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label small fw-semibold" htmlFor="pay-attachment">
                    Pièce jointe (reçu, capture écran…)
                  </label>
                  <input
                    id="pay-attachment"
                    type="file"
                    className="form-control form-control-sm"
                    accept="image/*,.pdf,application/pdf"
                    onChange={(e) => void handleAttachment(e)}
                  />
                  {attachment && (
                    <div className="small text-success mt-1">
                      Joint : {attachment.fileName}
                      {attachment.mimeType.startsWith("image/") && (
                        <img
                          src={attachment.dataUrl}
                          alt={attachment.fileName}
                          className="d-block mt-2 rounded border"
                          style={{ maxHeight: "120px" }}
                        />
                      )}
                    </div>
                  )}
                  {attachmentError && (
                    <div className="small text-danger mt-1">{attachmentError}</div>
                  )}
                </div>

                <div className="d-flex flex-wrap gap-2 ph-cashier-actions">
                  <button type="submit" className="btn btn-success btn-sm" disabled={busy}>
                    Confirmer le paiement
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => printBillingDocument(selected)}
                  >
                    Imprimer
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => setSelected(null)}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="ph-invoice-sheet">
              <p className="text-muted mb-0 small">
                Sélectionnez un document dans la file d&apos;attente pour enregistrer le
                paiement et créditer la caisse ou le compte bancaire / Mobile Money.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
